// Owner Decision 137 item 22: "Never show raw technical errors (regex patterns, field names). Every failure says in
// plain language what went wrong and what happens next or what Roelof should do." The reader side: every reason the
// pipeline stores on a document (document.set_outcome), returns (result.reason) or asks about when a read could not
// finish goes through plainReadingFailure, so no schema pattern, zod wording, parser message, error class, field name or
// path reaches a screen, a notice or a chat answer. The screen side (how the Inbox shows it) is lane I's.
import { NEXT_STEPS, nextStepFor } from "../messages.ts";

/** What stopped a read: the schema check's issues (with the dates the normaliser could not read), a thrown error, or an
 * action the database refused ({ code, message }). */
export type ReadingFailureCause =
  | { issues: { path: PropertyKey[]; message: string; code?: string }[]; unreadable?: { field: string; raw: string }[] }
  | { code: string; message: string }
  | Error
  | unknown;

export type PlainReadingFailure = { code: string; what: string; next: string; message: string };

/** Field names as a person says them. Anything not listed is spelt out from its key (snake_case to words). */
const FIELD_WORDS: Record<string, string> = {
  invoice_date: "invoice date", delivery_date: "delivery date", credit_date: "credit note date", payment_date: "payment date", document_date: "document date",
  release_date: "release date", capture_date: "capture date", auction_date: "auction date",
  invoice_number: "invoice number", credit_number: "credit note number", note_number: "note number", document_number: "document number",
  total_incl: "total including VAT", total_excl: "total excluding VAT", vat: "VAT amount", vat_rate: "VAT rate", balance_due: "balance due",
  amount: "amount paid", amount_incl: "amount including VAT", price_excl: "price", unit_price_excl: "unit price",
  lines: "lots", items: "lots", lot_numbers: "lot numbers", lot_number: "lot number", species_raw: "species", description_raw: "description",
  males: "number of males", females: "number of females", unknown_sex: "number of animals of unknown sex", count: "number of animals",
  issuer: "issuer", addressed_to: "buyer", buyer: "buyer", seller: "seller", payer: "payer", payee: "payee", reference: "reference", bank_reference: "bank reference",
  invoice_reference: "invoice reference", auction_name: "auction", terms_wording: "payment terms", signature_present: "signature", pages_read: "number of pages read",
  confidence: "confidence", notes: "notes", doc_type: "kind of document", kind: "kind", tags_raw: "tags", page: "page", name: "name", vat_no: "VAT number"
};
const DATE_FIELD = /(^|_)date$/;
const words = (key: string) => FIELD_WORDS[key] ?? key.replace(/_/g, " ");

/** A path into the reading as a person says it: lines.0.price_excl -> "the price of lot 1"; total_incl -> "the total including VAT". */
export function fieldInWords(path: PropertyKey[]): string {
  const parts = path.map(String);
  if (!parts.length) return "the document";
  const index = parts.findIndex((p) => /^\d+$/.test(p));
  if (index > 0) {
    const n = Number(parts[index]) + 1;
    const item = parts[index - 1] === "lot_numbers" ? `lot number ${n}` : `lot ${n}`;
    const rest = parts.slice(index + 1).filter((p) => !/^\d+$/.test(p));
    return rest.length ? `the ${rest.map(words).join(" ")} of ${item}` : item;
  }
  return `the ${parts.filter((p) => !/^\d+$/.test(p)).map(words).join(" ")}`;
}

/** A value as printed, quoted, capped so a long misread never floods a reason, with no quote marks of its own inside. */
const printed = (raw: string) => {
  const flat = raw.replace(/["“”]/g, "'").replace(/\s+/g, " ").trim();
  return `"${flat.length > 40 ? `${flat.slice(0, 40).trimEnd()}…` : flat}"`;
};

const isIssues = (c: unknown): c is { issues: { path: PropertyKey[]; message: string; code?: string }[]; unreadable?: { field: string; raw: string }[] } =>
  !!c && typeof c === "object" && Array.isArray((c as { issues?: unknown }).issues);
const isRefusal = (c: unknown): c is { code: string; message: string } =>
  !!c && typeof c === "object" && !(c instanceof Error) && typeof (c as { code?: unknown }).code === "string" && typeof (c as { message?: unknown }).message === "string";

/**
 * The plain words for a read that could not finish: { code, what, next, message }. code is a NEXT_STEPS key starting
 * with read_ (or the refused action's own code), next is NEXT_STEPS[code], and message is `${what} ${next}`. The file
 * name is always named; a value as printed is quoted; a field is said in words, never by its key or path.
 */
export function plainReadingFailure(cause: ReadingFailureCause, filename: string): PlainReadingFailure {
  const file = `"${filename}"`;
  const done = (code: string, what: string): PlainReadingFailure => {
    const next = nextStepFor(code);
    return { code, what, next, message: `${what} ${next}` };
  };
  if (isIssues(cause)) {
    const unreadable = new Map((cause.unreadable ?? []).map((u) => [u.field, u.raw]));
    // A date the normaliser could not read: named as printed, whether or not the schema check also reports it.
    const [firstUnreadable] = unreadable;
    if (firstUnreadable) {
      const [field, raw] = firstUnreadable;
      return done("read_date_unreadable", `On ${file} ${fieldInWords(field.split("."))} is printed as ${printed(raw)}, which is not a date that can be read.`);
    }
    const dateIssue = cause.issues.find((i) => DATE_FIELD.test(String(i.path[i.path.length - 1] ?? "")));
    if (dateIssue) return done("read_date_missing", `${capital(fieldInWords(dateIssue.path))} is not printed on ${file}.`);
    const named = [...new Set(cause.issues.slice(0, 3).map((i) => {
      const field = fieldInWords(i.path);
      const plural = /\blots\b|\bnumbers\b|\btags\b/.test(field);
      const missing = /received (undefined|null)\b|\bRequired\b/i.test(i.message);
      const empty = /too_small/i.test(i.code ?? "") || /at least/i.test(i.message);
      return `${field} ${missing ? (plural ? "are missing" : "is missing") : empty ? (plural ? "are empty" : "is empty") : "could not be read"}`;
    }))];
    return done("read_incomplete", `${file} could not be read completely: ${named.length ? named.join("; ") : "a value could not be read"}.`);
  }
  if (isRefusal(cause)) return done(cause.code, `Reading ${file} stopped: ${cause.message.trim()}`);
  const text = cause instanceof Error ? cause.message : typeof cause === "string" ? cause : "";
  if (cause instanceof SyntaxError || /\bJSON\b|could not be understood|declined to read|too long to read|could not be recognised|Unexpected (token|end)/i.test(text)) {
    const what = /declined/i.test(text) ? `RK Intelligence declined to read ${file}.`
      : /too long/i.test(text) ? `${file} was too long to read in one go.`
      : /recognised/i.test(text) ? `The kind of document ${file} could not be recognised.`
      : `The reading of ${file} came back in a form that could not be understood.`;
    return done("read_not_understood", what);
  }
  return done("read_stopped", `Reading ${file} stopped before it was finished.`);
}

const capital = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

/** The read_* next steps, for callers that list them. */
export const READ_NEXT_STEPS = Object.fromEntries(Object.entries(NEXT_STEPS).filter(([k]) => k.startsWith("read_")));
